import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { OpenAiResponse } from 'src/interfaces';
import QueryInput from 'src/components/atoms/query_input';
import { setThreadFocus } from 'src/redux/slices/open_ai';
import { trackEvent } from 'src/analytics';
import { EVENTS, SCREENS } from 'src/analytics/events';
import { getFaviconUrl } from 'src/helpers/rest';

import copyDarkIcon from 'src/assets/images/copy_dark.svg';
import audioIcon from 'src/assets/images/audio.svg';
import audioStopIcon from 'src/assets/images/audio_stop.svg';
import downArrow from 'src/assets/images/down_arrow.svg';
import upperIcon from 'src/assets/images/upper_icon.svg';
import closeIcon from 'src/assets/images/close_icon.svg';
import LandingIcon from 'src/assets/images/landing_page.gif';

import styles from './index.module.scss';
import { useAudioPlayer } from 'src/hooks/useAudioPlayer';
import { getAudioUri } from 'src/helpers/voice';

interface ConversationComponentProps {
    openAIResponse: OpenAiResponse[];
    onPressStartStream: () => void;
    setOpenAIResponse: React.Dispatch<React.SetStateAction<OpenAiResponse[]>>;
};

const ConversationComponent: React.FunctionComponent<ConversationComponentProps> = props => {
    const dispatch = useDispatch();
    const conversationContainerRef = useRef(null);

    const { openAIResponse: openAIResponses, onPressStartStream } = props ?? {};

    const [copied, setCopied] = useState<Array<number>>([]);
    const [copiedIndex, setCopiedIndex] = useState<number>(-1);
    const [width, setWidth] = useState<number>(window.innerWidth);
    const [autoScroll, setAutoScroll] = useState<boolean>(true);
    const [openSourceIndex, setOpenSourceIndex] = useState<number>(-1);
    
    const { playAudio, stopAudio, audioIndex, isPlaying, isAPILoading } = useAudioPlayer({
        getAudioUri: getAudioUri
    });

    useEffect(() => {
        const handleResize = () => {
            setWidth(window.innerWidth);
        };
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        if (copiedIndex !== null) {
            setTimeout(() => {
                setCopied((prevCopied) => {
                    const indexToRemove = prevCopied.indexOf(copiedIndex);
                    if (indexToRemove !== -1) {
                        const temp = [...prevCopied];
                        temp.splice(indexToRemove, 1);
                        return temp;
                    }
                    return prevCopied;
                });
                setCopiedIndex(-1);
            }, 3000);
        }
    }, [copiedIndex]);

    useEffect(() => {
        if (!conversationContainerRef.current) return;

        const container = conversationContainerRef.current as HTMLDivElement;
        if (container && autoScroll) {
            container.scrollTop = container.scrollHeight;
            container.scrollTo({
                top: container.scrollHeight,
                behavior: 'smooth'
            });
        }
    }, [openAIResponses, conversationContainerRef, autoScroll]);

    const handleCopyClick = (index: number) => {
        trackEvent(EVENTS.CONVERSATION_SCREEN.TAP_COPY_ICON, {
            screen_name: SCREENS.LANDING_PAGE,
            utm: true,
            search: window.location.search
        });

        const element = document.getElementById(`textarea-answer-${index}`);
        if (element) {
            const range = document.createRange();
            range.selectNode(element);
            const selection = window.getSelection();
            if (selection) {
                selection.removeAllRanges();
                selection.addRange(range);
                document.execCommand('copy');
                selection.removeAllRanges();
                setCopied([...copied, index]);
                setCopiedIndex(index);
            }
        }
    };
    
    const onPressEvent = (sourceLink: string) => {
        trackEvent(EVENTS.CONVERSATION_SCREEN.SELECT_SOURCE_FILE, {
            selected_source: sourceLink,
            screen_name: SCREENS.LANDING_PAGE,
            utm: true,
            search: window.location.search
        });
        if (window.parent && window.parent !== window) {
            window.parent.postMessage({
                type: "OPEN_LINK",
                url: sourceLink
            }, "*");
        } else {
            window.open(sourceLink, '_blank');
        }
    }

    const clickOnArrow = (index: number) => {
        if (index === openSourceIndex) {
            setOpenSourceIndex(-1);
        } else {
            setOpenSourceIndex(index);
        }
    };

    const handleScroll = () => {
        if (conversationContainerRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = conversationContainerRef.current;
            const isAtBottom = scrollHeight - scrollTop === clientHeight;
            setAutoScroll(isAtBottom);
        }
    };

    return (
        <div className={styles.conversation}>
            {width < 900 &&
                <div className={styles.coversation_mobile_header}>
                    <img className={styles.conversation_header_image} src={closeIcon} alt='close_icon' onClick={() => dispatch(setThreadFocus(true))} />
                    <div className={styles.conversation_mobile_heading}>Thread</div>
                </div>
            }
            <div className={styles.conversation_wrapper} ref={conversationContainerRef} onScroll={handleScroll}>
                {(openAIResponses || []).map((openAIResponse: OpenAiResponse, index: number) => (
                    <div className={styles.conversion_main_container}>
                        <div className={styles.conversation_container} style={openAIResponse?.status === 'error' ? { border: '1px solid #F10000' } : {}}>
                            <span className={styles.user_message} style={openAIResponse?.status === 'error' ? { color: '#F10000' } : {}}>{openAIResponse?.query}</span>
                            {(width < 900 && openAIResponse?.sources) &&
                                <div className={styles.mobile_sources_main_wrapper}>
                                    <div className={styles.mobile_sources_wrapper}>
                                        <div className={styles.sources_img_arrow_group}>
                                            <div className={styles.mobile_sources_header}>
                                                <span className={styles.mobile_source_heading}>Sources</span>
                                                <div className={styles.mobile_source_heading_right}>
                                                    <div className={styles.sources_images}>
                                                        {Object.keys(openAIResponse.sources.ArticleTitle).slice(0, 3).map((source, index) => (
                                                            <div key={index} className={styles.source_group}>
                                                                <img
                                                                    className={styles.source_absolute_image}
                                                                    src={getFaviconUrl(source, index)}
                                                                    alt='source'
                                                                />
                                                            </div>
                                                        ))}
                                                        <div key={index} className={styles.source_group} style={{ background: '#FFE8AE', marginLeft: '10px', borderRadius: '50%', border: '1px solid #E8DCC1' }}>
                                                            {Object.keys(openAIResponse.sources.ArticleTitle).length > 3
                                                                ? <span style={{ fontSize: '13px', padding: '0px 2px' }}>{`+${Object.keys(openAIResponse.sources.ArticleTitle).length - 3}`}</span>
                                                                : <div style={{ marginRight: '5px' }}></div>
                                                            }
                                                        </div>
                                                    </div>

                                                </div>
                                            </div>
                                            <img className={styles.mobile_sources_arrow} src={(index === openSourceIndex) ? upperIcon : downArrow} alt='mobile_source_arrow' onClick={() => clickOnArrow(index)} />
                                        </div>
                                        {(index === openSourceIndex && openAIResponse?.sources?.ArticleTitle) && <>
                                            {<div className={styles.mobile_sources_contents}>
                                                {Object.keys(openAIResponse.sources.ArticleTitle).map((source, idx) => (
                                                    <div className={styles.mobile_source_box} onClick={() => onPressEvent(source)}>
                                                        <img
                                                            style={{ height: '24px', width: '24px', borderRadius: '50%' }}
                                                            src={getFaviconUrl(source, idx)}
                                                            alt='source'
                                                        />
                                                        <span className={styles.source_content}>{openAIResponse?.sources?.ArticleTitle[source]}</span>
                                                        <div style={{ display: 'flex', gap: '5px', alignItems: 'center', marginTop: '5px', marginBottom: '3px' }}>
                                                            <span className={styles.source_link}>{source}</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>}
                                        </>}

                                        {(index === openSourceIndex && Object.keys(openAIResponse.sources.ArticleTitle).length > 2) &&
                                            <div className={styles.mobile_sliding_dots}>
                                                {[1, 2, 3].map(() => (
                                                    <div className={styles.dot}></div>
                                                ))}
                                            </div>
                                        }
                                    </div>
                                </div>
                            }

                            <div className={styles.answer_wrapper}>
                                {/*Answer Content */}
                                {openAIResponse?.answer &&
                                    <div className={styles.answer_container}>
                                        <div className={styles.answer_header}>
                                            {openAIResponse?.status !== 'error' &&
                                                <div className={styles.answer_header_left}>
                                                    <span className={styles.answer_heading}>Answer</span>
                                                </div>
                                            }
                                            {(!openAIResponse.loader && openAIResponse?.status !== 'error') &&
                                                <div className={styles.icon_wrapper}>
                                                    {!!(isAPILoading && audioIndex === index) && <>
                                                        <span className={styles.loader_text} >Generating voice...</span>
                                                        <img src={LandingIcon} className={styles.loading_icon} alt='loading' />
                                                    </>}
                                                    {!!(isPlaying && audioIndex === index) &&
                                                        <img src={audioStopIcon} className={styles.audio_stop_icon} onClick={stopAudio} alt='stop' />
                                                    }
                                                    {((!isPlaying && !isAPILoading) || audioIndex !== index) &&
                                                        <img src={audioIcon}
                                                            className={styles.audio_icon}
                                                            onClick={() => playAudio((openAIResponse?.answer || '').replace(/<br>/g, ' '), index)}
                                                            alt='play'
                                                        />
                                                    }
                                                    {copied.length && copied.includes(index)
                                                        ? <span className={styles.copy_text}>Copied!</span>
                                                        : <img src={copyDarkIcon} className={styles.copy_icon} alt='answer_icon' onClick={() => handleCopyClick(index)} />
                                                    }
                                                </div>}
                                        </div>
                                        {/* <div id={`textarea-answer-${index}`} className={styles.answer} dangerouslySetInnerHTML={{ __html: openAIResponse?.answer }}></div> */}
                                        <div id={`textarea-answer-${index}`} className={styles.answer} dangerouslySetInnerHTML={{ __html: openAIResponse?.answer.replace(/\*\*|\ -\ /g, '') }}></div>
                                    </div>
                                }
                            </div>

                            {openAIResponse.loader &&
                                <div className={styles.loader_text_wrapper}>
                                    <img src={LandingIcon} alt='landing_icon' className={styles.landing_icon} />

                                    {openAIResponse?.loading_text &&
                                        <div className={styles.blinking_text}>{openAIResponse?.loading_text}</div>
                                    }
                                </div>
                            }

                            {/* <div className={styles.related_wrapper}>
                                <span className={styles.related_heading}>Related</span>

                                <div className={styles.related_content} style={{ marginTop: '13px' }}>
                                    <span className={styles.related_content_text}>what is the iap vaccination schedule for infants</span>
                                    <img src={plusIcon} alt={'plus_icon'} className={styles.plus_icon} />
                                </div>

                                <div className={styles.related_content}>
                                    <span className={styles.related_content_text}>what is the iap guidebook</span>
                                    <img src={plusIcon} alt={'plus_icon'} className={styles.plus_icon} />
                                </div>

                                <div className={styles.related_content}>
                                    <span className={styles.related_content_text}>what are the recommended vaccines for children according to iap</span>
                                    <img src={plusIcon} alt={'plus_icon'} className={styles.plus_icon} />
                                </div>
                            </div> */}
                        </div>

                        {!!(width > 900 && openAIResponse?.sources) &&
                            <div className={styles.sources_wrapper}>
                                {/*Sources content */}
                                {openAIResponse?.sources && <>
                                    <div className={styles.sources_header}>
                                        <span className={styles.sources_heading}>Sources</span>
                                    </div>

                                    {openAIResponse?.sources?.ArticleTitle && <>
                                        <div className={styles.sources_contents}>
                                            {Object.keys(openAIResponse.sources.ArticleTitle).map((source, idx) => (
                                                <div className={styles.source_container} onClick={() => onPressEvent(source)}>
                                                    {/* <img style={{ height: '24px', width: '24px' }} src={SOURCES_ICONS[idx % SOURCES_ICONS.length]} alt='source' /> */}
                                                    <img
                                                        style={{ height: '24px', width: '24px', borderRadius: '50%' }}
                                                        src={getFaviconUrl(source, idx)}
                                                        alt='source'
                                                    />
                                                    <span className={styles.source_content}>{openAIResponse?.sources?.ArticleTitle[source]}</span>
                                                    <div style={{ display: 'flex', gap: '5px', alignItems: 'center', marginTop: '5px', marginBottom: '3px' }}>
                                                        <span className={styles.source_heading}>{source}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </>}
                                </>}
                            </div>
                        }
                    </div>
                ))}
            </div>

            <div className={styles.query_input_wrapper}>
                <div className={styles.query_input}>
                    <QueryInput onPressStartStream={onPressStartStream} />
                </div>

                {width > 900 && <div className={styles.query_div}></div>}
            </div>
        </div>
    );
};

export default ConversationComponent;